'use client';

import { Volume, Volume2, VolumeX } from 'lucide-react';

import React, { useCallback, useMemo } from 'react';

import { Slider } from '@/components/ui/slider';

import { cn } from '@/lib/utils';

import { useHLSPlayer } from '../contexts/hls-player-provider';
import type { IControlsProps } from '../types';

function VolumeControl({ className }: IControlsProps) {
	const { audioState, setVolume, toggleMute } = useHLSPlayer();
	const { volume, isMuted } = audioState;

	const displayVolume = useMemo(() => (isMuted ? 0 : volume), [isMuted, volume]);

	const handleVolumeChange = useCallback(
		(value: number[]) => {
			setVolume(value[0]);
		},
		[setVolume]
	);

	return (
		<div className={cn('group', className)}>
			<div className="flex items-center gap-1 rounded-md px-1 py-0 transition-all duration-300 group-hover:bg-white/20">
				<div
					className={cn(
						'flex h-4 w-0 items-center overflow-hidden transition-[width] duration-300 ease-in-out group-hover:w-20 group-hover:delay-500',
						'group-hover:w-16'
					)}
				>
					<Slider
						value={[displayVolume]}
						onValueChange={handleVolumeChange}
						max={100}
						step={1}
						className="relative"
						trackClassName="h-1 !bg-white/30 cursor-pointer"
						rangeClassName="!bg-white cursor-pointer"
						thumbClassName="size-2 border-0 bg-white absolute -translate-x-1/2 -translate-y-1/2"
						aria-label="Volume"
					/>
				</div>

				<button
					onClick={toggleMute}
					className="flex h-7 shrink-0 items-center justify-center rounded-md text-white transition-colors"
					aria-label={isMuted ? 'Unmute' : 'Mute'}
				>
					{isMuted || volume === 0 ? (
						<VolumeX size={16} />
					) : volume < 20 ? (
						<Volume size={16} />
					) : (
						<Volume2 size={16} />
					)}
				</button>
			</div>
		</div>
	);
}

export default React.memo(VolumeControl);
